import { useEffect, useState } from 'react'

// App-wide confirmation toast — EVERY completed mutation confirms visibly
// (create/edit/cancel/save/resend...). One host, mounted once in App; call
// showToast() from anywhere. Design: the same pill used ad hoc before, now
// shared so no flow ships silent again (cert-walk discipline, 2026-07-04).
let listener: ((msg: string) => void) | null = null
let clearListener: (() => void) | null = null
let shownAt = 0
let survivedOnce = false

export function showToast(message: string): void {
  shownAt = Date.now()
  survivedOnce = false
  listener?.(message)
}

/**
 * Called on every route change: a toast fired FOR that navigation (age
 * < 500ms, e.g. "Partido creado" right before landing on Mis partidos)
 * survives it; anything older is stale and must not bleed onto the next
 * screen.
 */
export function clearToastOnRouteChange(): void {
  // A toast may accompany exactly ONE navigation (the one it announces);
  // any further route change clears it immediately (no multi-screen bleed).
  if (Date.now() - shownAt <= 500 && !survivedOnce) {
    survivedOnce = true
    return
  }
  clearListener?.()
}

export function ToastHost() {
  const [msg, setMsg] = useState<string | null>(null)

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined
    listener = (m) => {
      setMsg(m)
      clearTimeout(timer)
      timer = setTimeout(() => setMsg(null), 2200)
    }
    clearListener = () => {
      clearTimeout(timer)
      setMsg(null)
    }
    return () => {
      listener = null
      clearListener = null
      clearTimeout(timer)
    }
  }, [])

  if (!msg) return null
  return (
    <p
      role="status"
      className="um-enter fixed bottom-[var(--floating-bottom)] left-1/2 -translate-x-1/2 z-[70] max-w-[85vw] truncate px-4 py-2 rounded-full bg-[var(--surface-card)] border border-[var(--line)] text-[length:var(--fs-sm)] font-medium shadow-lg"
    >
      {msg}
    </p>
  )
}
